import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, CalendarDays, Clock, Package, Users, MapPin } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PKG_COLORS = {
  'Create Party': '#FF3B6B',
  'Celebrate Party': '#06D6A0',
  'Signature Edible Party': '#FFD166',
};
const TIME_LABELS = { '10:00': '10 AM', '13:00': '1 PM', '16:00': '4 PM' };

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getMonthDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();
  const days = [];
  const prevLast = new Date(year, month, 0).getDate();
  for (let i = startPad - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month - 1, prevLast - i), isCurrentMonth: false });
  }
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push({ date: new Date(year, month, d), isCurrentMonth: true });
  }
  const remaining = 42 - days.length;
  for (let d = 1; d <= remaining; d++) {
    days.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
  }
  return days;
};

/**
 * Personal calendar for an employee — read-only.
 *
 * Renders a month grid with two layered indicators per day:
 *  • a coloured pill for each *assigned party* (click → modal with details)
 *  • a striped clock badge for each *scheduled shift* (click → modal)
 *
 * The component is presentational — the caller fetches `shifts` and `parties`
 * from /api/employee/shifts and /api/employee/my-parties respectively.
 *
 * `large` prop bumps cell heights for use as a primary view (vs. inline panel).
 */
export default function EmployeeCalendarView({ shifts = [], parties = [], large = false }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [partyOpen, setPartyOpen] = useState(null);
  const [shiftOpen, setShiftOpen] = useState(null);

  const todayStr = fmtDate(today);
  const monthDays = useMemo(() => getMonthDays(year, month), [year, month]);
  const monthName = useMemo(
    () => new Date(year, month).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }),
    [year, month],
  );

  // Group by date (YYYY-MM-DD) for fast cell lookups
  const partiesByDate = useMemo(() => {
    const map = new Map();
    (parties || []).forEach((p) => {
      const arr = map.get(p.date) || [];
      arr.push(p); map.set(p.date, arr);
    });
    return map;
  }, [parties]);
  const shiftsByDate = useMemo(() => {
    const map = new Map();
    (shifts || []).forEach((s) => {
      const arr = map.get(s.date) || [];
      arr.push(s); map.set(s.date, arr);
    });
    return map;
  }, [shifts]);

  const goPrev = () => {
    if (month === 0) { setYear(year - 1); setMonth(11); } else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 11) { setYear(year + 1); setMonth(0); } else setMonth(month + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4" data-testid="employee-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#FF3B6B]" />
          <h3 className={`font-bold text-[#1E1E24] ${large ? 'text-xl sm:text-2xl' : 'text-base'}`} style={{ fontFamily: 'Fredoka, sans-serif' }}>{monthName}</h3>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={goPrev} className="h-8 w-8 p-0 rounded-full" data-testid="emp-cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} className="h-8 px-3 rounded-full text-xs" data-testid="emp-cal-today">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goNext} className="h-8 w-8 p-0 rounded-full" data-testid="emp-cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-[#6B6B7A] mb-3">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#FF3B6B]" /> Assigned party</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-[#118AB2]" /> Scheduled shift</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#FFD166]" /> Today</span>
      </div>

      {/* Day-of-week header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS.map((d) => (
          <div key={d} className="text-[10px] font-bold text-center text-[#6B6B7A] uppercase tracking-wider py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 gap-1">
        {monthDays.map((day, idx) => {
          const dateStr = fmtDate(day.date);
          const dayParties = partiesByDate.get(dateStr) || [];
          const dayShifts = shiftsByDate.get(dateStr) || [];
          const isToday = dateStr === todayStr;
          const muted = !day.isCurrentMonth;
          // When `large`, give cells more vertical room (and on mobile too).
          const cellMin = large ? 'min-h-[110px] sm:min-h-[130px]' : 'min-h-[72px]';
          return (
            <div
              key={idx}
              className={`${cellMin} rounded-lg border p-1.5 flex flex-col gap-0.5 transition ${
                muted
                  ? 'bg-[#FAFAF7] border-[#F0EDE6] opacity-60'
                  : isToday
                    ? 'bg-[#FFD166]/10 border-[#FFD166]'
                    : 'bg-white border-[#F0EDE6] hover:border-[#E8E5DF]'
              }`}
              data-testid={`emp-cal-day-${dateStr}`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-bold ${large ? 'text-sm' : 'text-[11px]'} ${isToday ? 'text-[#B8860B]' : muted ? 'text-[#9a9aa6]' : 'text-[#1E1E24]'}`}>
                  {day.date.getDate()}
                </span>
              </div>
              <div className={`space-y-0.5 flex-1 ${large ? 'overflow-hidden' : ''}`}>
                {/* Shifts shown FIRST so the start/end time is the most prominent
                    daily info, matching the user's "see shifts and parties" ask. */}
                {dayShifts.slice(0, large ? 2 : 1).map((s) => (
                  <button
                    key={s.shift_id || `${s.date}-${s.start_time}`}
                    type="button"
                    onClick={() => setShiftOpen(s)}
                    className="w-full text-left text-[9px] truncate rounded px-1 py-0.5 font-semibold text-white flex items-center gap-1"
                    style={{ background: '#118AB2' }}
                    data-testid={`emp-cal-shift-${s.shift_id || `${s.date}-${s.start_time}`}`}
                    title={`Shift ${s.start_time}-${s.end_time}${s.notes ? ` · ${s.notes}` : ''}`}
                  >
                    <Clock className="w-2.5 h-2.5 flex-shrink-0" />
                    <span className="truncate">{s.start_time}-{s.end_time}</span>
                  </button>
                ))}
                {dayParties.slice(0, large ? 3 : 2).map((p) => (
                  <button
                    key={p.booking_id}
                    type="button"
                    onClick={() => setPartyOpen(p)}
                    className="w-full text-left text-[9px] truncate rounded px-1 py-0.5 font-semibold text-white"
                    style={{ background: PKG_COLORS[p.package_name] || '#FF3B6B' }}
                    data-testid={`emp-cal-party-${p.booking_id}`}
                    title={`${p.child_name || p.customer_name} — ${TIME_LABELS[p.time_slot] || p.time_slot}`}
                  >
                    {TIME_LABELS[p.time_slot] || p.time_slot} · {p.child_name || p.customer_name || 'Party'}
                  </button>
                ))}
                {(dayParties.length + dayShifts.length) > (large ? 5 : 3) && (
                  <button
                    type="button"
                    onClick={() => setPartyOpen(dayParties[0] || null) || setShiftOpen(dayShifts[0] || null)}
                    className="text-[9px] text-[#6B6B7A] hover:text-[#1E1E24] underline"
                  >
                    +{(dayParties.length + dayShifts.length) - (large ? 5 : 3)} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Read-only party detail modal */}
      <AnimatePresence>
        {partyOpen && (
          <PartyDetailModal party={partyOpen} onClose={() => setPartyOpen(null)} />
        )}
      </AnimatePresence>
      {/* Read-only shift detail modal */}
      <AnimatePresence>
        {shiftOpen && (
          <ShiftDetailModal shift={shiftOpen} onClose={() => setShiftOpen(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

const PartyDetailModal = ({ party, onClose }) => {
  const accent = PKG_COLORS[party.package_name] || '#FF3B6B';
  const dateLabel = useMemo(() => {
    if (!party.date) return '';
    return new Date(party.date + 'T00:00:00').toLocaleDateString('en-CA', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  }, [party.date]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="emp-party-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-2 h-8 rounded-full" style={{ background: accent }} />
            {party.package_name || 'Party'}
          </DialogTitle>
          <DialogDescription>
            <span className="text-xs text-[#6B6B7A]">Read-only view — contact your manager to make changes</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-sm">
          <Row icon={CalendarDays} label="Date">
            {dateLabel}
            {party.time_slot && (
              <Badge variant="outline" className="ml-2 text-[10px] border-[#E8E5DF]">
                {TIME_LABELS[party.time_slot] || party.time_slot}
              </Badge>
            )}
          </Row>
          <Row icon={Users} label="Birthday child">
            {party.child_name || '—'}
            {party.child_age && <span className="text-[#6B6B7A] ml-1">({party.child_age} yrs)</span>}
          </Row>
          <Row icon={MapPin} label="Customer">
            <div>
              <p>{party.customer_name || '—'}</p>
              {party.customer_phone && <p className="text-xs text-[#6B6B7A]">{party.customer_phone}</p>}
            </div>
          </Row>
          {Number(party.guest_count) > 0 && (
            <Row icon={Users} label="Guests">
              {party.guest_count}
            </Row>
          )}
          {(party.add_ons || []).length > 0 && (
            <Row icon={Package} label="Add-ons">
              <div className="flex flex-wrap gap-1">
                {(party.add_ons || []).map((a, idx) => (
                  <Badge key={idx} variant="outline" className="text-[10px] border-[#E8E5DF] text-[#6B6B7A]">
                    {(a.name || a.label || a.id) + (Number(a.quantity || a.qty || 1) > 1 ? ` ×${a.quantity || a.qty}` : '')}
                  </Badge>
                ))}
              </div>
            </Row>
          )}
          <div className="flex items-center gap-2 pt-2">
            {party.arrived && <Badge className="bg-[#06D6A0]/15 text-[#06D6A0] border-none text-[10px]">Arrived</Badge>}
            {party.party_completed && <Badge className="bg-[#9333EA]/15 text-[#9333EA] border-none text-[10px]">Completed</Badge>}
            {party.deposit_paid && <Badge className="bg-[#FFD166]/20 text-[#B8860B] border-none text-[10px]">Deposit paid</Badge>}
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={onClose} variant="outline" className="rounded-full" data-testid="emp-party-modal-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-[#6B6B7A] mt-0.5 flex-shrink-0" />
    <div className="flex-1">
      <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-semibold">{label}</p>
      <div className="text-[#1E1E24]">{children}</div>
    </div>
  </div>
);

const ShiftDetailModal = ({ shift, onClose }) => {
  const dateLabel = useMemo(() => {
    if (!shift?.date) return '';
    return new Date(shift.date + 'T00:00:00').toLocaleDateString('en-CA', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  }, [shift]);
  // Compute hours from start/end
  const hoursLabel = useMemo(() => {
    if (!shift?.start_time || !shift?.end_time) return null;
    const [sh, sm] = String(shift.start_time).split(':').map(Number);
    const [eh, em] = String(shift.end_time).split(':').map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    if (Number.isNaN(mins) || mins <= 0) return null;
    return (mins / 60).toFixed(2) + 'h';
  }, [shift]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="emp-shift-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-2 h-8 rounded-full" style={{ background: '#118AB2' }} />
            Scheduled Shift
          </DialogTitle>
          <DialogDescription>
            <span className="text-xs text-[#6B6B7A]">Read-only — your manager publishes the schedule.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-sm">
          <Row icon={CalendarDays} label="Date">{dateLabel}</Row>
          <Row icon={Clock} label="Time">
            <span className="font-mono">{shift.start_time}</span>{' – '}
            <span className="font-mono">{shift.end_time}</span>
            {hoursLabel && <Badge variant="outline" className="ml-2 text-[10px] border-[#E8E5DF]">{hoursLabel}</Badge>}
          </Row>
          {shift.role && (
            <Row icon={Users} label="Role">
              <span className="capitalize">{String(shift.role).replace(/_/g, ' ')}</span>
            </Row>
          )}
          {shift.notes && (
            <Row icon={Package} label="Notes">
              <p className="whitespace-pre-line">{shift.notes}</p>
            </Row>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={onClose} variant="outline" className="rounded-full" data-testid="emp-shift-modal-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
